import React, { useState, useEffect } from "react";
import { array, bool, number, string } from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";

import Typography from "../shared/Typography";
import ClosedPackage from "./shared/ClosedPackage";
import PackageDetail from "./shared/PackageDetail";
import PackageDetailDescription from "./shared/PackageDetailDescription";
import ServicesDetail from "./shared/ServicesDetail";
import ShownPackage from "./shared/ShownPackage";
import ScrollableContainer from "../shared/ScrollableContainer";
import CollapsableContainer from "@components/shared/CollapsableContainer";

import { getOptionAssetURL } from "@lib/getOptionAssetURL";
import { toggleElementInArray } from "@lib/toggleElementInArray";
import { CUSTOMIZE_TYPES } from "@shared/constants";
import { updateCarConfigurationAction } from "@redux/actions/dealActions";
import { setLastChangedAtAction } from "@redux/actions/deprecatedActions";
import gtm from "@gtm/core";

import {
  useCarConfigurationSelector,
  useActiveCarPreconfigurationSelector,
} from "@redux/reducers/deal";

import {
  openCustomizeSectionEvent,
  openCustomizeDetailEvent,
  changeSelectionEvent,
} from "@gtm/events/homepage";

const CustomizeItem = ({
  defaultOpen = false,
  disabled = false,
  packages,
  readOnly,
  title,
  type,
}) => {
  const [isOpen, setOpenCustomize] = useState(defaultOpen);
  const [openPackageId, setOpenPackageId] = useState();
  const openPackage = packages.find(pack => pack.id === openPackageId);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const configuration = useCarConfigurationSelector();
  const activePreconfiguration = useActiveCarPreconfigurationSelector();
  const selectedItems = configuration[type] ?? [];

  const isTypeService = type === CUSTOMIZE_TYPES.SERVICE;

  const onPackageClick = id =>
    setOpenPackageId(currentValue => (currentValue === id ? null : id));

  const updateCarConfiguration = async option => {
    if (type === CUSTOMIZE_TYPES.SERVICE) {
      const isRemove = selectedItems.some(({ id }) => id === option.id);

      await dispatch(
        updateCarConfigurationAction({ [type]: isRemove ? [] : [option] })
      );

      dispatch(setLastChangedAtAction(Date.now()));

      return;
    }

    await dispatch(
      updateCarConfigurationAction({
        [type]: toggleElementInArray(selectedItems, option),
      })
    );

    dispatch(setLastChangedAtAction(Date.now()));
  };

  const changeConfiguration = async option => {
    await updateCarConfiguration(option);

    setOpenPackageId(null);

    setOpenCustomize(defaultOpen);

    const isSelected = !selectedItems.some(pack => pack.id === openPackageId);

    gtm.fire(
      changeSelectionEvent({
        action: isSelected ? "Add" : "Remove",
        label: option.label,
      })
    );
  };

  const hasDescription = activePreconfiguration.description?.length > 0;

  useEffect(() => {
    setOpenPackageId(hasDescription ? null : packages[0]?.id);
    setOpenCustomize(defaultOpen);
  }, [activePreconfiguration]);

  const singlePackage = packages.length === 1;

  const onCollapsableContainerChange = isContainerOpen => {
    setOpenCustomize(!isContainerOpen);

    if (!isContainerOpen) {
      gtm.fire(openCustomizeSectionEvent(t(`customize.${title ?? type}`)));
    }
  };

  return (
    <CollapsableContainer
      data-id={`customize-item-container-${type}`}
      className={{
        bodyContainer: "flex flex-col gap-6",
      }}
      isOpen={isOpen}
      isDisabled={disabled}
      defaultOpen={defaultOpen}
      onChange={onCollapsableContainerChange}
      title={
        <>
          <span
            className="flex justify-between items-center gap-4"
            data-id={`${type}-selected-count`}
          >
            <span>{t(`customize.${title ?? type}`)}</span>
            {!readOnly && (
              <span className="text-lg font-semibold">
                {selectedItems.length}
                <sup className="text-xs pr-4">/{packages.length}</sup>
              </span>
            )}
          </span>
        </>
      }
    >
      {type === CUSTOMIZE_TYPES.PACK && activePreconfiguration.description && (
        <Typography>
          <PackageDetailDescription
            description={activePreconfiguration.description}
          />
        </Typography>
      )}
      <ScrollableContainer isVisible={isOpen}>
        {packages
          .sort((a, b) => {
            // Services should not be sorted by selected ones
            // as only one service can be selected at the time
            if (isTypeService) {
              return 0;
            }

            return (
              selectedItems.some(pack => pack.id === b.id) -
              selectedItems.some(pack => pack.id === a.id)
            );
          })
          .map(({ duration, id, label, type: packType }, index) =>
            openPackage?.id === id ? (
              <ShownPackage
                onClick={() => onPackageClick(openPackage.id)}
                key={index}
                title={openPackage.label}
                backgroundUrl={`url("${getOptionAssetURL(
                  `/${packType}/${isTypeService ? index + 1 : id}_big.jpg`,
                  activePreconfiguration.id
                )}")`}
                preventClose={!hasDescription && singlePackage}
              />
            ) : (
              openPackage?.id === undefined && (
                <ClosedPackage
                  onClick={() => {
                    onPackageClick(id);
                    gtm.fire(
                      openCustomizeDetailEvent({
                        action: t(`customize.${title ?? packType}`),
                        label,
                      })
                    );
                  }}
                  key={index}
                  title={label}
                  type={packType}
                  duration={isTypeService ? duration : 0}
                  backgroundUrl={
                    !isTypeService &&
                    `url("${getOptionAssetURL(
                      `/${packType}/${id}${singlePackage ? "_big" : ""}.jpg`,
                      activePreconfiguration.id
                    )}")`
                  }
                  isSelected={
                    packType === CUSTOMIZE_TYPES.SERVICE
                      ? selectedItems.some(pack => pack.groupLabel === label)
                      : selectedItems.some(pack => pack.id === id)
                  }
                  singleItem={packages.length === 1}
                />
              )
            )
          )}
      </ScrollableContainer>
      <AnimatePresence initial={false}>
        {openPackage?.id && (
          <motion.div
            variants={{
              open: {
                opacity: 1,
                height: "auto",
                display: "block",
              },
              collapsed: {
                opacity: 0,
                height: 0,
                transitionEnd: { display: "none" },
                transition: { duration: 0.3, type: "tween" },
              },
            }}
            animate="open"
            initial="collapsed"
            exit="collapsed"
          >
            {!isTypeService ? (
              <PackageDetail
                changeConfiguration={changeConfiguration}
                isSelected={
                  !!selectedItems.find(pack => pack.id === openPackage.id)
                }
                option={openPackage}
                readOnly={readOnly}
                withGallery={
                  type === CUSTOMIZE_TYPES.PACK ||
                  (type === CUSTOMIZE_TYPES.ACCESSORY &&
                    !!openPackage.personalizationOptions.length)
                }
              />
            ) : (
              <ServicesDetail
                availableServices={openPackage.services}
                onServiceButtonClick={changeConfiguration}
                selectedServices={selectedItems}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </CollapsableContainer>
  );
};

CustomizeItem.propTypes = {
  defaultOpen: bool,
  disabled: bool,
  type: string.isRequired,
  packages: array.isRequired,
  index: number,
  readOnly: bool,
  singleItem: bool,
  title: string,
};

export default CustomizeItem;
